// Story structure templates for the outline board.
// Each template provides column titles and ordered beat cards (title + description).
// UI strings are in pt-BR; code comments in English.

export interface BeatCard {
  title: string;
  description: string;
}

export interface TemplateColumn {
  title: string;
  beats: BeatCard[];
}

export interface StoryStructure {
  id: string;
  name: string;
  description: string;
  columns: TemplateColumn[];
}

// ---------------------------------------------------------------------------
// Três Atos
// ---------------------------------------------------------------------------

const tresAtos: StoryStructure = {
  id: 'tres-atos',
  name: 'Três Atos',
  description: 'A estrutura clássica dividida em Setup, Confrontação e Resolução.',
  columns: [
    {
      title: 'Ato I — Setup',
      beats: [
        {
          title: 'Gancho',
          description:
            'Cena de abertura impactante que captura a atenção do leitor e estabelece o tom da história.',
        },
        {
          title: 'Incidente Incitante',
          description:
            'Evento que perturba o mundo do protagonista e desencadeia o conflito principal.',
        },
        {
          title: 'Ponto de Virada 1',
          description:
            'Decisão ou acontecimento que lança o protagonista ao Ato II — sem possibilidade de retorno ao status quo.',
        },
      ],
    },
    {
      title: 'Ato II — Confrontação',
      beats: [
        {
          title: 'Ponto Médio',
          description:
            'Falso triunfo ou falso fracasso que redefine os objetivos do protagonista e eleva os riscos.',
        },
        {
          title: 'Ponto de Virada 2',
          description:
            'Crise máxima: tudo parece perdido e o protagonista enfrenta sua maior provação interior.',
        },
      ],
    },
    {
      title: 'Ato III — Resolução',
      beats: [
        {
          title: 'Clímax',
          description:
            'Confronto final onde o protagonista aplica sua transformação para resolver o conflito de forma definitiva.',
        },
        {
          title: 'Desfecho',
          description:
            'Novo equilíbrio. Consequências do clímax, estado final dos personagens e fechamento emocional.',
        },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Save the Cat! (Blake Snyder — 15 beats)
// ---------------------------------------------------------------------------

const saveTheCat: StoryStructure = {
  id: 'save-the-cat',
  name: 'Save the Cat!',
  description: 'Os 15 beats de Blake Snyder — o roteiro-fórmula mais usado em Hollywood.',
  columns: [
    {
      title: 'Ato 1',
      beats: [
        {
          title: 'Imagem de Abertura',
          description:
            'Cena única que captura o estado "antes" do protagonista e o tom geral da obra. Será espelhada na Imagem Final.',
        },
        {
          title: 'Tema Declarado',
          description:
            'Alguém diz ao protagonista (sem que ele ainda perceba) qual é a verdade central que terá de aprender.',
        },
        {
          title: 'Apresentação',
          description:
            'Apresentação do mundo cotidiano do protagonista: seus defeitos, rotinas e o que ele precisa (mas não sabe).',
        },
        {
          title: 'Catalisador',
          description:
            'Evento externo que tira o protagonista da estase e o obriga a agir — a "bomba" que explode o status quo.',
        },
        {
          title: 'Debate',
          description:
            'O protagonista questiona se deve aceitar o chamado. Dúvidas, medos e hesitações antes da decisão definitiva.',
        },
      ],
    },
    {
      title: 'Ato 2',
      beats: [
        {
          title: 'Entrada no Ato 2',
          description:
            'O protagonista toma a decisão e entra em um mundo novo e mais arriscado — o "mundo de cabeça para baixo".',
        },
        {
          title: 'História B',
          description:
            'Nova relação (geralmente romântica ou de mentor) que carrega o tema e ajuda o protagonista a crescer.',
        },
        {
          title: 'Diversão e Jogos',
          description:
            'A promessa da premissa: o protagonista vive as aventuras e momentos icônicos que o leitor esperava.',
        },
        {
          title: 'Ponto Médio',
          description:
            'Falso triunfo ou falso fracasso que eleva os riscos e força o protagonista a mudar de abordagem.',
        },
        {
          title: 'Os Vilões se Aproximam',
          description:
            'As forças antagonistas apertam o cerco; a equipe ou o plano do protagonista começa a rachar.',
        },
        {
          title: 'Tudo Está Perdido',
          description:
            'Pior momento da história: morte simbólica (ou real) e sensação de derrota total para o protagonista.',
        },
        {
          title: 'Noite Escura da Alma',
          description:
            'Momento de desespero e reflexão profunda; o protagonista toca o fundo antes de descobrir a saída.',
        },
      ],
    },
    {
      title: 'Ato 3',
      beats: [
        {
          title: 'Entrada no Ato 3',
          description:
            'Ideia ou epifania — frequentemente inspirada pela História B — que aponta o caminho para o clímax.',
        },
        {
          title: 'Clímax',
          description:
            'Confronto final onde o protagonista aplica o tema aprendido para vencer definitivamente o conflito.',
        },
        {
          title: 'Imagem Final',
          description:
            'Espelho da Imagem de Abertura: mostra a transformação completa do protagonista e o novo mundo.',
        },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Jornada do Herói (Campbell / Vogler — 12 estágios)
// ---------------------------------------------------------------------------

const jornadaDoHeroi: StoryStructure = {
  id: 'jornada-do-heroi',
  name: 'Jornada do Herói',
  description:
    'Os 12 estágios da monomito de Joseph Campbell adaptados por Christopher Vogler.',
  columns: [
    {
      title: 'Partida',
      beats: [
        {
          title: 'Mundo Comum',
          description:
            'O herói é apresentado em seu mundo cotidiano, estabelecendo o contraste com a aventura vindoura.',
        },
        {
          title: 'Chamado à Aventura',
          description:
            'Um problema, desafio ou oportunidade chama o herói a sair de sua zona de conforto.',
        },
        {
          title: 'Recusa do Chamado',
          description:
            'O herói hesita ou recusa o chamado por medo, senso de dever ou insegurança.',
        },
        {
          title: 'Encontro com o Mentor',
          description:
            'Uma figura de sabedoria fornece ao herói conselho, equipamento ou confiança para seguir em frente.',
        },
        {
          title: 'Travessia do Limiar',
          description:
            'O herói decide partir e cruza a fronteira para o Mundo Especial, sem garantia de retorno.',
        },
      ],
    },
    {
      title: 'Iniciação',
      beats: [
        {
          title: 'Provas, Aliados e Inimigos',
          description:
            'O herói enfrenta testes, faz amizades e identifica adversários, aprendendo as regras do Mundo Especial.',
        },
        {
          title: 'Aproximação da Caverna Oculta',
          description:
            'O herói se prepara para o maior desafio, reunindo forças e confrontando seus medos mais profundos.',
        },
        {
          title: 'Provação',
          description:
            'Crise central e morte simbólica: o herói enfrenta seu maior medo e parece derrotado antes de renascer.',
        },
        {
          title: 'Recompensa',
          description:
            'O herói sobrevive à provação e conquista a recompensa — objeto, conhecimento ou poder almejado.',
        },
      ],
    },
    {
      title: 'Retorno',
      beats: [
        {
          title: 'Caminho de Volta',
          description:
            'O herói inicia o retorno ao Mundo Comum, muitas vezes perseguido pelas consequências de seus atos.',
        },
        {
          title: 'Ressurreição',
          description:
            'Clímax final: o herói é purificado em um último teste de vida ou morte que prova sua transformação.',
        },
        {
          title: 'Retorno com o Elixir',
          description:
            'O herói volta ao Mundo Comum trazendo a recompensa — um tesouro, conhecimento ou cura para todos.',
        },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Exported collection
// ---------------------------------------------------------------------------

export const STORY_STRUCTURES: StoryStructure[] = [tresAtos, saveTheCat, jornadaDoHeroi];
